/**
 * A throttle keyed on the pair (client, root): leading edge, a minimum
 * interval, and a trailing edge that runs the latest call (spec ruling 6).
 *
 * WHY THE PAIR. The broker's client and a section's client hold separate
 * caches (`platform-scope-provider.tsx`), so one must never suppress the
 * other's refetch. The client half is a WeakMap key, so a dropped client takes
 * its state with it.
 *
 * WHY A TRAILING EDGE. A burst's first event fires at once; the rest of the
 * burst collapses into one call at the end of the window, so the last write in
 * the burst is always seen. A burst of any length inside one window gives at
 * most two calls per pair.
 */

interface RootState {
  lastFired: number
  timer: ReturnType<typeof setTimeout> | undefined
  latest: (() => unknown) | undefined
}

export interface RootThrottle {
  /** Run `fire` now if the pair is outside its window, else hold it for the trailing edge. Returns what `fire` returned when it ran now. */
  readonly run: (client: object, rootId: string, fire: () => unknown) => unknown
  /** Drop every held trailing call for this client. */
  readonly cancel: (client: object) => void
}

export const createRootThrottle = (intervalMs: number): RootThrottle => {
  const states = new WeakMap<object, Map<string, RootState>>()

  const stateFor = (client: object, rootId: string): RootState => {
    let byRoot = states.get(client)
    if (byRoot === undefined) {
      byRoot = new Map()
      states.set(client, byRoot)
    }
    let state = byRoot.get(rootId)
    if (state === undefined) {
      state = { lastFired: Number.NEGATIVE_INFINITY, timer: undefined, latest: undefined }
      byRoot.set(rootId, state)
    }
    return state
  }

  const trail = (state: RootState): void => {
    state.timer = undefined
    const fire = state.latest
    state.latest = undefined
    if (fire === undefined) return
    state.lastFired = Date.now()
    void fire()
  }

  const run = (client: object, rootId: string, fire: () => unknown): unknown => {
    const state = stateFor(client, rootId)
    const now = Date.now()
    if (state.timer === undefined && now - state.lastFired >= intervalMs) {
      state.lastFired = now
      return fire()
    }
    state.latest = fire
    if (state.timer === undefined) {
      state.timer = setTimeout(() => trail(state), state.lastFired + intervalMs - now)
    }
    return undefined
  }

  const cancel = (client: object): void => {
    for (const state of states.get(client)?.values() ?? []) {
      if (state.timer !== undefined) clearTimeout(state.timer)
      state.timer = undefined
      state.latest = undefined
    }
  }

  return {
    run,
    cancel,
  }
}
